import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import {
  APP_DOWNLOAD_TAGLINE,
  APP_STORE_URL,
  PLAY_STORE_URL,
} from '../../config/appStoreLinks';

type Variant = 'footer' | 'card' | 'compact';

const variantStyles: Record<
  Variant,
  { wrap: string; tagline: string; play: string; iosNote: string }
> = {
  footer: {
    wrap: 'space-y-3',
    tagline: 'text-gray-400 text-sm leading-relaxed',
    play:
      'inline-flex items-center justify-center gap-2 rounded-lg bg-white text-gray-900 px-4 py-2.5 text-sm font-semibold hover:bg-gray-100 transition-colors w-full sm:w-auto',
    iosNote: 'text-gray-500 text-xs leading-relaxed',
  },
  card: {
    wrap: 'space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800',
    tagline: 'text-slate-600 text-sm leading-relaxed dark:text-slate-300',
    play:
      'inline-flex items-center justify-center gap-2 rounded-xl bg-[#1d3557] text-white px-4 py-3 text-sm font-semibold hover:bg-[#274c77] transition-colors w-full',
    iosNote: 'text-slate-500 text-xs dark:text-slate-400',
  },
  compact: {
    wrap: 'space-y-2',
    tagline: 'text-slate-600 text-xs leading-snug dark:text-slate-300',
    play:
      'inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#e63946] text-white px-3 py-2 text-xs font-semibold hover:bg-[#c1121f] transition-colors',
    iosNote: 'text-slate-500 text-[0.6875rem]',
  },
};

export const AppDownloadButtons: React.FC<{
  variant?: Variant;
  showTagline?: boolean;
  className?: string;
}> = ({ variant = 'card', showTagline = true, className = '' }) => {
  if (Capacitor.isNativePlatform()) {
    return null;
  }

  const styles = variantStyles[variant];

  return (
    <div className={`${styles.wrap} ${className}`.trim()}>
      {showTagline && <p className={styles.tagline}>{APP_DOWNLOAD_TAGLINE}</p>}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center flex-wrap">
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.play}
        >
          Google Play 下載
          <ExternalLink className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
        </a>
        {APP_STORE_URL ? (
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={
              variant === 'footer'
                ? 'inline-flex items-center justify-center gap-2 rounded-lg border border-gray-600 text-gray-200 px-4 py-2.5 text-sm font-semibold hover:bg-gray-800 transition-colors w-full sm:w-auto'
                : 'inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-700 w-full'
            }
          >
            App Store 下載
            <ExternalLink className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
          </a>
        ) : (
          <p className={styles.iosNote}>
            iPhone 請用 Safari 開啟 techfix.com.tw 發案；App Store 版審核通過後將開放下載。
          </p>
        )}
      </div>
    </div>
  );
};

export default AppDownloadButtons;
